/**
 * Axios API client with token management, CSRF injection, and 401 retry.
 *
 * Access token: read from tokenManager (in-memory only).
 * CSRF token: injected on state-changing requests via X-CSRF-Token header.
 * 401 handling: attempts silent refresh before redirecting to login.
 *
 * (Monday Item ID: 11501542831)
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tokenManager } from '@/services/tokenManager';

/**
 * API error class for handling API errors with structured error information.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Dig a human-readable message string out of an arbitrary parsed error body.
 *
 * Backends return several shapes — a bare string, `{message}`, `{detail}`
 * (FastAPI HTTPException), `{error}`, or a nested `{message: {error, message}}`
 * (the #321 structured-error shape). Without this, the interceptor would pass
 * an object straight to `String(...)` and surface "[object Object]".
 */
export function extractErrorMessage(body: unknown, fallback = 'An error occurred'): string {
  if (typeof body === 'string') return body.trim() ? body : fallback;
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    for (const key of ['message', 'detail', 'error'] as const) {
      const v = obj[key];
      if (typeof v === 'string' && v.trim()) return v;
      if (v && typeof v === 'object') {
        const nested = extractErrorMessage(v, '');
        if (nested) return nested;
      }
    }
  }
  return fallback;
}

/** Methods that mutate state and require CSRF protection. */
const STATE_CHANGING_METHODS = ['post', 'put', 'patch', 'delete'];

/**
 * Auth endpoints that must NOT trigger the 401 refresh-retry (#350).
 *
 * A 401 from `/auth/refresh` is the "no valid session" signal itself — retrying
 * it re-invokes `refreshAccessToken()` while the original refresh is still
 * in-flight, which deduplicates onto the same pending promise and hangs
 * `initializeAuth()` forever. That left unauthenticated users stuck on the
 * loading spinner instead of being redirected to `/login`. A 401 from
 * `/auth/login` means bad credentials and must surface to the form rather than
 * be masked by a refresh-retry. Both reject cleanly instead.
 */
const REFRESH_RETRY_EXEMPT_ENDPOINTS = ['/auth/refresh', '/auth/login'];

/** True when the request targets an endpoint exempt from the 401 refresh-retry. */
const isRefreshRetryExempt = (url?: string): boolean =>
  !!url && REFRESH_RETRY_EXEMPT_ENDPOINTS.some((endpoint) => url.includes(endpoint));

/**
 * Public, token-scoped pages reached unauthenticated via an emailed deep link.
 * A background 401 (e.g. the execution-notifications poll mounted app-wide) must
 * NOT bounce these to `/login` — that would make the magic-link set-password and
 * the password-reset flows unreachable from their email links (#372).
 */
const PUBLIC_NO_REDIRECT_PATHS = [
  '/login',
  '/set-password',
  '/forgot-password',
  '/reset-password',
];

/** True when the current page is a public route that must not be redirected to /login on a 401. */
export const isPublicNoRedirectPath = (pathname: string): boolean =>
  PUBLIC_NO_REDIRECT_PATHS.some((p) => pathname.includes(p));

/**
 * Create and configure the Axios client for API requests.
 * Base URL is /api/v1 which is proxied to http://localhost:8000 in development.
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: '/api/v1',
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
  });

  // Request interceptor — add auth token and CSRF header
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Inject access token from tokenManager (in-memory)
      const token = tokenManager.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Inject CSRF token for state-changing requests
      const csrfToken = tokenManager.getCsrfToken();
      if (csrfToken && config.headers && STATE_CHANGING_METHODS.includes(config.method ?? '')) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor — 403 CSRF retry, 401 retry-after-refresh, then redirect
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response) {
        let data = error.response.data as any;
        // A blob error body (from responseType:'blob' requests like dataset export) carries
        // the JSON error payload as an undecoded Blob — decode it so extractErrorMessage can
        // read the real backend message instead of surfacing a generic "An error occurred". (#651)
        // Duck-type check (not instanceof) is required because jsdom and Node.js each ship their
        // own Blob constructor — instanceof fails across those two contexts in the test environment.
        if (data != null && typeof data === 'object' && typeof (data as any).text === 'function') {
          try {
            const text = await data.text();
            try {
              data = JSON.parse(text);
            } catch {
              data = text; // non-JSON body: surface the raw text
            }
          } catch {
            /* keep the blob; extractErrorMessage falls back gracefully */
          }
        }
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _csrfRetry?: boolean };

        // Handle 403 CSRF mismatch — retry once with fresh cookie token
        if (
          error.response.status === 403 &&
          originalRequest &&
          !originalRequest._csrfRetry &&
          typeof data?.detail === 'string' &&
          data.detail.toLowerCase().includes('csrf')
        ) {
          originalRequest._csrfRetry = true;
          return client(originalRequest);
        }

        // Handle 401 Unauthorized — attempt silent refresh before redirect.
        // Auth endpoints (refresh/login) are exempt: a 401 there is the genuine
        // "no session" / "bad credentials" signal, and retrying /auth/refresh
        // deadlocks initialization (#350). Let those reject cleanly.
        if (
          error.response.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isRefreshRetryExempt(originalRequest.url)
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await tokenManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          } catch {
            // Refresh also failed — clear state and redirect
            tokenManager.clearTokens();

            // Lazy import to avoid circular dependency
            const { useAuthStore } = await import('@/store/auth');
            useAuthStore.setState({ user: null, isAuthenticated: false });

            if (!isPublicNoRedirectPath(window.location.pathname)) {
              window.location.href = '/login';
            }
            throw new ApiError('Authentication required.', 401, 'UNAUTHORIZED');
          }
        }

        console.error('API Error Response:', JSON.stringify(data, null, 2));
        throw new ApiError(
          extractErrorMessage(data),
          error.response.status,
          data?.code,
          data?.details ?? data?.detail ?? data?.message ?? data
        );
      } else if (error.request) {
        // Request made but no response received
        throw new ApiError('No response from server', undefined, 'NETWORK_ERROR');
      } else {
        // Error setting up the request
        throw new ApiError(error.message, undefined, 'REQUEST_ERROR');
      }
    }
  );

  return client;
};

export const apiClient = createApiClient();

/**
 * Helper function to handle file uploads with progress tracking.
 */
export const uploadFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.post('/ingestion/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};
